import React, {useContext, useEffect, useState} from 'react';
import { useLocation } from 'react-router';
import { AppContext } from 'Context/AppContext';
import { DATA_CONFIGURATION, DECIMAL_CONVERSION, DEFAULT_VALUE, ENTER, FIELD_CLASS, INCREMENT, LABLE_FRACTION, LABLE_WHOLE_NUMBER, MANAGE, MAX, MIN, MIN_TOOL_TIP, UNITS } from '../ISRConstants';
import { TConnector, TField, TFieldClass, TFieldDataConfiguration, TIndexState, TProductCategory, TSubmodel, TUpsertISRProjectDataModelsInput } from '../Pages/DefineModel/model';
import FractionalInput from './FractionalInput';
import {convertToDecimal} from '../ISRHelper';
import {getAllProjectClass} from '../Pages/DefineModel/ModelServices/ModalServices';
import {useISRContext} from '../Hooks/useISRContext';
import Card from 'Components/DesignSystem/Card/Card';
import TextField from 'Components/DesignSystem/TextField/TextField';
import Toggle from 'Components/DesignSystem/Toggle/Toggle';
import Typography from 'Components/DesignSystem/Typography/Typography';
import FieldTitle from 'Components/DesignSystem/FieldTitle/FieldTitle';
import ConfigurationFilterComponent from "./ConfigurationFilterComponent";
import FieldClassSelector from './FieldClassSelector';

type TFractionConfigurationProps = {
    id: string;
    selectedItem: TField | TSubmodel | TProductCategory | TConnector | null;
    updateFieldDataConfiguration: (updatedConfig: TFieldDataConfiguration) => void;
    userProjectDataModels: TUpsertISRProjectDataModelsInput;
    indexInfo: TIndexState;
    projectId?: string;
}

const FractionConfiguration = ({id, selectedItem, updateFieldDataConfiguration, userProjectDataModels, indexInfo, projectId }: TFractionConfigurationProps) => {
    const [fieldDataConfiguration, setFieldDataConfiguration] = useState<TFieldDataConfiguration>({});
    const [fieldName, setFieldName] = useState<string>('');
    const isrContext = useISRContext();
    const [errors, setErrors] = useState<{ [key: string]: string | null }>({ min: null, max: null, defaultValue: null, });
    const [showMinMaxError, setShowMinMax] = useState<boolean>(false);
    const location = useLocation();
    const isGuide = location.pathname.includes('/guide');
    const { enableConfigure } = useContext(AppContext);
    const isConfigureDisabled = isGuide && !enableConfigure;


    useEffect(() => {
        if (selectedItem && 'belongsToDataModel' in selectedItem && selectedItem?.belongsToDataModel) {
            setFieldName(userProjectDataModels?.project.dataModels[indexInfo?.dataModelIndex ?? -1]?.data.fields[indexInfo?.fieldIndex ?? -1].name ?? "");
        } else {
            setFieldName(userProjectDataModels?.project.fields[indexInfo?.fieldOutsideDataModelIndex ?? -1]?.data?.name ?? "");
        }
        const existingValue = selectedItem && 'dataConfiguration' in selectedItem && selectedItem?.dataConfiguration ? selectedItem.dataConfiguration : {}
        setFieldDataConfiguration(existingValue);
    }, [selectedItem]);

    useEffect(() => {
        const {min, max} = fieldDataConfiguration || {};
        // Fire whenever either field has a value. Mirror the displayed input defaults
        // (min ?? '0', max ?? '1') so editing Min alone still populates both decimals,
        // even while Max is still showing its default value (undefined in state).
        if (min || max) {
            const effectiveMin = (min ?? '0') as string;
            const effectiveMax = (max ?? '1') as string;
            setFieldDataConfiguration(prevConfig => ({
                ...prevConfig,
                decimalMin: convertToDecimal(effectiveMin),
                decimalMax: convertToDecimal(effectiveMax),
            }));
        }
    }, [fieldDataConfiguration?.min, fieldDataConfiguration?.max]);

    useEffect(() => {
        const {min, max} = fieldDataConfiguration || {};
        // Mirror the displayed input defaults (min ?? '0', max ?? '1') so the error fires even when only
        // Min is changed and Max is still showing its default value (undefined in state).
        const isEmpty = (value: number | string | undefined) => value === undefined || value === null || value === '';
        const effectiveMin = isEmpty(min) ? '0' : (min as string);
        const effectiveMax = isEmpty(max) ? '1' : (max as string);
        const hasError = convertToDecimal(effectiveMin) > convertToDecimal(effectiveMax);
        setShowMinMax(hasError);
        const hasChanges = Object.keys(fieldDataConfiguration).some(
            (key) => fieldDataConfiguration[key as keyof TFieldDataConfiguration] !== undefined
        );
        // Gate on the locally-computed hasError (not the showMinMaxError state, which updates a
        // render late) so an invalid min/max is never propagated to userProjectDataModels and
        // therefore never triggers the debounced auto-save.
        if (hasChanges && !hasError) {
            updateFieldDataConfiguration(fieldDataConfiguration);
        }
    }, [fieldDataConfiguration]);

    useEffect(() => {
        setFieldDataConfiguration((prevConfig) => ({...prevConfig, decimalConversion: true}));
    }, []);

    // useEffect(() => {
    //     if (fieldDataConfiguration?.filter?.attributeId) {
    //         fetchAssociatedAttributes(fieldDataConfiguration?.filter?.categoryId);
    //     }
    // }, [fieldDataConfiguration?.filter?.categoryId]);


    const handleMinChange = (input: string) => {
        setFieldDataConfiguration((prevConfig) => ({...prevConfig, min: input}));
    };

    const handleMaxChange = (input: string) => {
        setFieldDataConfiguration((prevConfig) => ({...prevConfig, max: input}));
    };

    const handleIncrementChange = (value: string) => {
        setFieldDataConfiguration((prevConfig) => ({...prevConfig, increment: value}));
    };

    const handleUnitsChange = (value: string) => {
        setFieldDataConfiguration((prevConfig) => ({...prevConfig, units: value}));
    };

    // const handleDecimalConversionChange = (value: boolean) => {
    //     const { min, max } = fieldDataConfiguration || {};
    //     if (value && min && max) {
    //         setFieldDataConfiguration(prevConfig => ({
    //             ...prevConfig,
    //             decimalMin: convertToDecimal(min as string),
    //             decimalMax: convertToDecimal(max as string),
    //         }));
    //     }
    //     setFieldDataConfiguration((prevConfig) => ({...prevConfig, decimalConversion: value})); 
    // };

    const handleDecimalMinChange = (value: string) => {
        setFieldDataConfiguration((prevConfig) => ({...prevConfig, decimalMin: value}));
    };

    const handleDecimalMaxChange = (value: string) => {
        setFieldDataConfiguration((prevConfig) => ({...prevConfig, decimalMax: value}));
    };

    const handleLabelWholeNumberChange = (value: string) => {
        setFieldDataConfiguration((prevConfig) => ({...prevConfig, labelWholeNumber: value}));
    };

    const handleLabelFractionChange = (value: string) => {
        setFieldDataConfiguration((prevConfig) => ({...prevConfig, labelFraction: value}));
    };

    const handleDefaultValueChange = (input: string) => {
        setFieldDataConfiguration((prevConfig) => ({...prevConfig, defaultValue: input}));
    };

    const handleClassChange = (selectedValue: string) => {
        const selectedObject = isrContext?.fieldClasses?.find((item: TFieldClass) => item.name === selectedValue);
        setFieldDataConfiguration((prevConfig) => ({...prevConfig, class: selectedObject}));
    };

    useEffect(() => {
        fetchAllClasses();
    }, []);

    const fetchAllClasses = async () => {
        try {
            const responseData = await getAllProjectClass(projectId ?? '');
            if (responseData?.done) {
                isrContext?.setFieldClasses(responseData?.data?.fieldClasses ?? []);
                isrContext?.setModelClasses(responseData?.data?.modelClasses ?? []);
            } else {
                //toast.error(responseData?.message);
            }
        } catch (error) {
            console.log('Error', error);
        }
    }

    return (
        <Card
            id={`${id}-data-config-component`}
            data-testid={`${id}-data-config-component`}
            title={DATA_CONFIGURATION}
        >
            <div id={`${id}-fraction-section`} className='tw-flex tw-items-start tw-gap-5'>
                <div id={`${id}-min-fraction`} className='tw-w-[15%]'>
                    <FieldTitle label={MIN} infoMessage={MIN_TOOL_TIP} data-testid={`${id}-min-fraction-label`} />
                    <FractionalInput
                        id={`${id}-min-fraction-input`}
                        data-testid={`${id}-min-fraction-input-field`}
                        ariaLabel={MIN}
                        fieldName='min'
                        value={fieldDataConfiguration?.min ?? '0'}
                        handleInputChange={handleMinChange}
                        errors={errors}
                        setErrors={setErrors}
                    />
                    {errors && errors?.min ? (
                        <Typography variant="caption-metadata" as="p" id={`${id}-min-fraction-error`} className='tw-text-tone-danger-ink tw-mt-1'>
                            {'Invalid fraction!'}
                        </Typography>
                    ) : null}
                    {showMinMaxError && (
                        <Typography variant="caption-metadata" as="p" id={`${id}-min-max-error`} data-testid={`${id}-min-max-error`} className='tw-text-tone-danger-ink tw-mt-1 tw-w-max tw-max-w-[12rem] tw-break-words'>
                            {'Minimum value cannot be greater than the maximum.'}
                        </Typography>
                    )}
                </div>
                <div id={`${id}-max-fraction`} className='tw-w-[15%]'>
                    <FieldTitle label={MAX} infoMessage={MIN_TOOL_TIP} data-testid={`${id}-max-fraction-label`} />
                    <FractionalInput
                        id={`${id}-max-fraction-input`}
                        data-testid={`${id}-max-fraction-input-field`}
                        ariaLabel={MAX}
                        fieldName='max'
                        value={fieldDataConfiguration?.max ?? '1'}
                        handleInputChange={handleMaxChange}
                        errors={errors}
                        setErrors={setErrors}
                    />
                    {errors && errors?.max ? (
                        <Typography variant="caption-metadata" as="p" id={`${id}-max-fraction-error`} className='tw-text-tone-danger-ink tw-mt-1'>
                            {'Invalid fraction!'}
                        </Typography>
                    ) : null}
                </div>
                <div id={`${id}-decimal-conversion`} className='tw-w-[20%] tw-mb-auto'>
                    {/* Reads `true` and ignores its own change event — a display-only
                        switch, exactly as before. Left as-is: making it live is a
                        product decision, not a migration. */}
                    <Toggle
                        id={`${id}-decimal-conversion-toggle`}
                        data-testid={`${id}-decimal-conversion-toggle`}
                        label={DECIMAL_CONVERSION}
                        labelPlacement="top"
                        checked={true}
                        onChange={() => {}}
                    />
                </div>
                <TextField
                    id={`${id}-decimal-min-input`}
                    data-testid={`${id}-decimal-min-input`}
                    label={MIN}
                    placeholder={ENTER}
                    disabled={true}
                    onChange={handleDecimalMinChange}
                    value={String(fieldDataConfiguration?.decimalMin ?? '')}
                    className='tw-w-[15%]'
                />
                <TextField
                    id={`${id}-decimal-max-input`}
                    data-testid={`${id}-decimal-max-input`}
                    label={MAX}
                    placeholder={ENTER}
                    disabled={true}
                    onChange={handleDecimalMaxChange}
                    value={String(fieldDataConfiguration?.decimalMax ?? '')}
                    className='tw-w-[15%]'
                />
            </div>

            <div id={`${id}-additional-inputs`} className='tw-flex tw-items-start tw-gap-5 tw-mt-5'>
                <TextField
                    id={`${id}-increment-input`}
                    data-testid={`${id}-increment-input`}
                    label={INCREMENT}
                    placeholder={ENTER}
                    onChange={handleIncrementChange}
                    value={String(fieldDataConfiguration?.increment ?? '')}
                    className='tw-w-[15%]'
                />
                <div id={`${id}-default-value`} className='tw-w-[15%]'>
                    <FieldTitle label={DEFAULT_VALUE} data-testid={`${id}-default-value-label`} />
                    <FractionalInput
                        id={`${id}-default-value-input`}
                        data-testid={`${id}-default-value-input-field`}
                        ariaLabel={DEFAULT_VALUE}
                        fieldName='defaultValue'
                        value={fieldDataConfiguration?.defaultValue ?? ''}
                        handleInputChange={handleDefaultValueChange}
                        errors={errors}
                        setErrors={setErrors}
                    />
                    {errors && errors?.defaultValue ? (
                        <Typography variant="caption-metadata" as="p" id={`${id}-default-value-error`} className='tw-text-tone-danger-ink'>
                            {'Invalid fraction!'}
                        </Typography>
                    ) : null}
                </div>
            </div>

            <div id={`${id}-labels-section`} className='tw-flex tw-items-start tw-gap-5 tw-mt-3'>
                <TextField
                    id={`${id}-units-input`}
                    data-testid={`${id}-units-input`}
                    label={UNITS}
                    placeholder={ENTER}
                    onChange={handleUnitsChange}
                    value={fieldDataConfiguration?.units ?? ''}
                    className='tw-w-[20%]'
                />
                <TextField
                    id={`${id}-whole-number-input`}
                    data-testid={`${id}-whole-number-input`}
                    label={LABLE_WHOLE_NUMBER}
                    placeholder={ENTER}
                    onChange={handleLabelWholeNumberChange}
                    value={fieldDataConfiguration?.labelWholeNumber ?? ''}
                    className='tw-w-[20%]'
                />
                <TextField
                    id={`${id}-fraction-label-input`}
                    data-testid={`${id}-fraction-label-input`}
                    label={LABLE_FRACTION}
                    placeholder={ENTER}
                    onChange={handleLabelFractionChange}
                    value={fieldDataConfiguration?.labelFraction ?? ''}
                    className='tw-w-[20%]'
                />
            </div>

            <div id={`${id}-decimal-info`} className="tw-w-auto tw-my-4 tw-px-2.5 tw-py-2 tw-bg-surface-sunken tw-rounded-ctl tw-border tw-border-edge tw-justify-start tw-items-center tw-gap-2.5 tw-inline-flex">
                <Typography variant="body-primary" id={`${id}-decimal-info-text`} className="tw-text-ink">
                    {`The associated decimal will be called "${fieldName} decimal"`}
                </Typography>
            </div>

            <div id={`${id}-config-filter`}>
                <ConfigurationFilterComponent
                    id={`${id}-config-filter-component`}
                    fieldDataConfiguration={fieldDataConfiguration}
                    setFieldDataConfiguration={setFieldDataConfiguration}
                    userProjectDataModels={userProjectDataModels}
                    indexInfo={indexInfo}
                    hasRange={true}
                />
            </div>

            <FieldClassSelector
                baseId={id}
                value={fieldDataConfiguration?.class?.name ?? ''}
                onChange={handleClassChange}
                disabled={isConfigureDisabled}
                projectId={projectId}
            />
        </Card>
    );
}

export default FractionConfiguration;